import { create } from 'zustand';

/**
 * Form state management store
 *
 * This store provides comprehensive form state management for complex
 * multi-step forms in the application. It handles form data, validation,
 * error states, and submission states for different form types.
 *
 * Key features:
 * - Generic form state management
 * - Multi-step form support
 * - Validation and error handling
 * - Form dirty state tracking
 * - Submission state management
 * - Type-safe form data handling
 */

/**
 * Generic form state interface
 *
 * Defines the common state structure for all forms including
 * data, errors, validation status, and submission state.
 *
 * @template T The type of form data
 */
interface FormState<T> {
  /** Current form data */
  data: T;
  /** Field-specific error messages */
  errors: Record<string, string>;
  /** Whether the form is currently valid */
  isValid: boolean;
  /** Whether the form has been modified */
  isDirty: boolean;
  /** Whether the form is currently being submitted */
  isSubmitting: boolean;
}

/**
 * Version form data interface
 *
 * Defines the data structure for file version forms including
 * file selection, target table, and validation options.
 */
export interface VersionFormData {
  /** Selected file for version */
  file: File | null;
  /** Target table ID */
  tableId: string;
  /** Validation options for the version */
  validationOptions: {
    skipDuplicates: boolean;
    validateSchema: boolean;
    autoDetectTypes: boolean;
  };
}

/**
 * Multi-step form state management interface
 *
 * Defines the complete form store that manages multiple form types
 * with their respective data, validation, and state management.
 * Provides both generic form actions and specific actions for
 * each form type.
 */
interface FormStore {
  // ===== FORM STATE =====
  /** Version form state */
  versionForm: FormState<VersionFormData>;

  // ===== GENERIC FORM ACTIONS =====
  /** Update form data and mark as dirty */
  updateFormData: <T>(formKey: keyof FormStore, data: Partial<T>) => void;
  /** Set error for a specific field */
  setFormError: (
    formKey: keyof FormStore,
    field: string,
    error: string
  ) => void;
  /** Clear error for a specific field */
  clearFormError: (formKey: keyof FormStore, field: string) => void;
  /** Set form validation status */
  setFormValid: (formKey: keyof FormStore, isValid: boolean) => void;
  /** Set form dirty status */
  setFormDirty: (formKey: keyof FormStore, isDirty: boolean) => void;
  /** Set form submission status */
  setFormSubmitting: (formKey: keyof FormStore, isSubmitting: boolean) => void;
  /** Reset form to initial state */
  resetForm: (formKey: keyof FormStore) => void;

  // ===== VERSION FORM SPECIFIC ACTIONS =====
  /** Set version file */
  setVersionFile: (file: File | null) => void;
  /** Set target table for version */
  setVersionTable: (tableId: string) => void;
  /** Set validation options for version */
  setValidationOptions: (options: VersionFormData['validationOptions']) => void;
  /** Validate version form */
  validateVersionForm: () => boolean;
}

/**
 * Form store implementation
 *
 * Creates a Zustand store for managing complex multi-step forms.
 * The store handles form data, validation, error states, and submission
 * states for file version forms.
 *
 * Key features:
 * - Type-safe form data management
 * - Multi-step form validation
 * - Error state management
 * - Form dirty state tracking
 * - Submission state handling
 * - Generic and specific form actions
 */
export const useFormStore = create<FormStore>((set, get) => ({
  // ===== FORM STATE INITIALIZATION =====

  /** Version form with default values */
  versionForm: {
    data: {
      file: null,
      tableId: '',
      validationOptions: {
        skipDuplicates: true,
        validateSchema: true,
        autoDetectTypes: true,
      },
    },
    errors: {},
    isValid: false,
    isDirty: false,
    isSubmitting: false,
  },

  // ===== GENERIC FORM ACTIONS =====

  /**
   * Update form data and mark as dirty
   *
   * Merges new data with existing form data and automatically
   * marks the form as dirty (modified).
   *
   * @param formKey The form to update
   * @param data Partial data to merge
   */
  updateFormData: <T>(formKey: keyof FormStore, data: Partial<T>) => {
    set((state) => {
      const formState = state[formKey] as FormState<T>;
      return {
        [formKey]: {
          ...formState,
          data: { ...formState.data, ...data },
          isDirty: true,
        },
      };
    });
  },

  /**
   * Set error for a specific field
   *
   * @param formKey The form to update
   * @param field Field name to set error for
   * @param error Error message
   */
  setFormError: <T>(
    formKey: keyof FormStore,
    field: keyof T,
    error: string
  ) => {
    set((state) => {
      const formState = state[formKey] as FormState<T>;
      return {
        [formKey]: {
          ...formState,
          errors: { ...formState.errors, [field]: error },
        },
      };
    });
  },

  /**
   * Clear error for a specific field
   *
   * @param formKey The form to update
   * @param field Field name to clear error for
   */
  clearFormError: <T>(formKey: keyof FormStore, field: keyof T) => {
    set((state) => {
      const formState = state[formKey] as FormState<T>;
      const { [field]: _, ...rest } = formState.errors;
      return {
        [formKey]: {
          ...formState,
          errors: rest,
        },
      };
    });
  },

  setFormValid: (formKey, isValid) => {
    set((state) => {
      const formState = state[formKey];
      if (typeof formState === 'object' && formState !== null && 'isValid' in formState) {
        return {
          [formKey]: {
            ...formState,
            isValid,
          },
        };
      }
      return state;
    });
  },

  setFormDirty: (formKey, isDirty) => {
    set((state) => {
      const formState = state[formKey];
      if (typeof formState === 'object' && formState !== null && 'isDirty' in formState) {
        return {
          [formKey]: {
            ...formState,
            isDirty,
          },
        };
      }
      return state;
    });
  },

  setFormSubmitting: (formKey, isSubmitting) => {
    set((state) => {
      const formState = state[formKey];
      if (typeof formState === 'object' && formState !== null && 'isSubmitting' in formState) {
        return {
          [formKey]: {
            ...formState,
            isSubmitting,
          },
        };
      }
      return state;
    });
  },

  resetForm: (formKey) => {
    set(() => {
      const initialData = {
        file: null,
        tableId: '',
        validationOptions: {
          skipDuplicates: true,
          validateSchema: true,
          autoDetectTypes: true,
        },
      };

      return {
        [formKey]: {
          data: initialData,
          errors: {},
          isValid: false,
          isDirty: false,
          isSubmitting: false,
        },
      };
    });
  },

  // ===== VERSION FORM SPECIFIC ACTIONS =====

  /** Set version file */
  setVersionFile: (file) => {
    get().updateFormData('versionForm', { file });
  },

  /** Set target table for version */
  setVersionTable: (tableId) => {
    get().updateFormData('versionForm', { tableId });
  },

  /** Set validation options for version */
  setValidationOptions: (validationOptions) => {
    get().updateFormData('versionForm', { validationOptions });
  },

  /**
   * Validate version form
   *
   * Checks that required fields are filled (file and table ID).
   *
   * @returns True if form is valid, false otherwise
   */
  validateVersionForm: () => {
    const { versionForm } = get();
    const { data } = versionForm;

    return data.file !== null && data.tableId.trim() !== '';
  },
}));
